import { useT } from '@/core/I18nProvider';
import { localizedPath } from '@/i18n/locale-path';

import { localeAlternates } from './locale-alternates';
import { useSeoMeta } from './use-seo-meta';

import type { PageMetaInput } from './page-meta';

/**
 * Per-route SEO head for the site-wide-localized marketing surface (GA-13 /
 * ADR-0133). A thin wrapper over {@link useSeoMeta} that resolves everything
 * locale-dependent from the active locale, so a page only declares its
 * **English-relative** path + (already-translated) title/description:
 *
 *   - `path`     → `localizedPath(enPath, locale)` (the locale-correct canonical)
 *   - `locale`   → the active locale (drives `og:locale`)
 *   - `alternates` → the full `hreflang` set (one per locale + `x-default`)
 *   - `breadcrumbLabels` → translated "Home" + section names for the JSON-LD
 *     `BreadcrumbList`
 *
 * `path` is ALWAYS the English-relative path (e.g. `/pricing`, `/vs/sierra`) —
 * never pre-prefixed; the hook applies the prefix. Title/description are passed
 * pre-translated (the page resolves them through `t()`), matching every existing
 * `useSeoMeta` call. Pass-through fields (`type`, `imageUrl`, `faq`, …) are
 * forwarded unchanged.
 */
export type LocalizedSeoInput = Omit<PageMetaInput, 'locale' | 'alternates' | 'breadcrumbLabels'>;

export function useLocalizedSeoMeta(input: LocalizedSeoInput): void {
  const { t, locale } = useT();
  useSeoMeta({
    ...input,
    path: localizedPath(input.path, locale),
    locale,
    alternates: localeAlternates(input.path),
    breadcrumbLabels: {
      home: t('seo.breadcrumb.home'),
      sections: {
        trust: t('seo.breadcrumb.section.trust'),
        vs: t('seo.breadcrumb.section.vs'),
        'one-pagers': t('seo.breadcrumb.section.onePagers'),
        pricing: t('seo.breadcrumb.section.pricing'),
        docs: t('seo.breadcrumb.section.docs'),
      },
    },
  });
}
